/**
 * Collapsible section headings — a small, consistent, persisted fold/unfold
 * behaviour shared across pages. Click (or Enter/Space) on a heading to fold
 * the content beneath it away; the collapsed/expanded state is remembered per
 * (page, section) key in localStorage so the player's layout sticks.
 *
 * Pages that already have their own disclosure mechanic (tabs, accordions,
 * dropdown selectors) should NOT use this — only flat stacks of sections.
 *
 * Usage:
 *   makeCollapsible(headingEl, contentEl, 'travel:foreign-shop');
 *
 * The heading gets a leading chevron, button semantics, and keyboard support.
 * No CSS file needed — minimal inline styles keep it self-contained.
 */

import { store } from '../state/store.js';

const STORE_PREFIX = 'rexmortis.collapse.';

// Keys are scoped per account so a shared browser (or a fresh character
// registered on a machine that played before) doesn't inherit the previous
// player's fold layout. The one-time cost: existing saved layouts reset when
// this shipped, since the key format changed.
function scopedKey(key: string): string {
  return `${STORE_PREFIX}${store.get().account?.id ?? 'anon'}.${key}`;
}

function isCollapsed(key: string): boolean {
  try {
    return localStorage.getItem(scopedKey(key)) === '1';
  } catch {
    return false;
  }
}
function persist(key: string, collapsed: boolean): void {
  try {
    if (collapsed) localStorage.setItem(scopedKey(key), '1');
    else localStorage.removeItem(scopedKey(key));
  } catch {
    /* storage unavailable — non-fatal */
  }
}

/**
 * Wire a heading element to fold/unfold a content element. `key` must be unique
 * per section (prefix it with the page name, e.g. 'profile:awards').
 */
export function makeCollapsible(
  heading: HTMLElement,
  content: HTMLElement,
  key: string,
): void {
  if (heading.dataset.collapsibleWired === '1') return; // idempotent
  heading.dataset.collapsibleWired = '1';

  let collapsed = isCollapsed(key);

  // Prepend a chevron if the heading doesn't already have one.
  let chev = heading.querySelector<HTMLElement>('.collapse-chev');
  if (!chev) {
    chev = document.createElement('span');
    chev.className = 'collapse-chev';
    chev.style.cssText =
      'display:inline-block; width:1em; margin-right:4px; transition:transform .15s; opacity:0.7;';
    heading.insertBefore(chev, heading.firstChild);
  }

  heading.setAttribute('role', 'button');
  heading.setAttribute('tabindex', '0');
  heading.style.cursor = 'pointer';
  heading.style.userSelect = 'none';

  const apply = (): void => {
    content.style.display = collapsed ? 'none' : '';
    chev!.textContent = collapsed ? '▸' : '▾';
    heading.setAttribute('aria-expanded', String(!collapsed));
  };
  apply();

  const toggle = (): void => {
    collapsed = !collapsed;
    persist(key, collapsed);
    apply();
  };
  heading.addEventListener('click', toggle);
  heading.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggle();
    }
  });
}

/**
 * Convenience: given a container, treat each direct-child heading (matching
 * `headingSel`) as a collapsible toggle for everything between it and the next
 * heading. Useful for innerHTML-built pages where sections aren't wrapped.
 *
 * Wraps the sibling run in a <div> so it can be hidden as a unit.
 */
export function autoCollapseSiblings(
  container: HTMLElement,
  headingSel: string,
  keyPrefix: string,
): void {
  const headings = Array.from(container.querySelectorAll<HTMLElement>(headingSel));
  headings.forEach((h, i) => {
    // Gather siblings until the next heading.
    const group = document.createElement('div');
    let node = h.nextElementSibling;
    const next = headings[i + 1] ?? null;
    while (node && node !== next) {
      const after = node.nextElementSibling;
      // Leave global <style>/<script> nodes where they are — folding them into
      // a hidden group would be pointless and could move page CSS around.
      if (node.tagName !== 'STYLE' && node.tagName !== 'SCRIPT') {
        group.appendChild(node);
      }
      node = after;
    }
    h.insertAdjacentElement('afterend', group);
    const key = `${keyPrefix}:${(h.textContent || i).toString().trim().slice(0, 40)}`;
    makeCollapsible(h, group, key);
  });
}
